import { Route, BrowserRouter as Router, Routes } from 'react-router-dom';
import './App.css';

import LoginForm from './pages/login';
import Pay from './pages/pay';
import ChangePrice from './pages/tiendas/cambiar-precio';
import CPC from './components/CuentasPorCobrar';
import DeletePayment from './pages/tiendas/eliminar-pago';
import DeleteSale from './pages/deleteSale';
import Ventas from './pages/reportes/ventas';
import Transacciones from './pages/reportes/transacciones';
import Eliminados from './pages/reportes/registros-eliminados';
import Layout from './components/Layout';
import DelinquentUsers from './pages/reportes/delincuentes';
import RecuperacionCartera from './pages/recuperacion-cartera';
import Internal from './pages/internal';
import Inicio from './pages/inicio';
import Sucursales from './pages/sucursales';
import MetodosDePago from './pages/metodos-de-pago';
import LogInAs from './pages/log-in-as';
import Help from './pages/ayuda';
import CambiarTelefono from './pages/tiendas/cambiar-telefono';
import InformationBancaria from './pages/informacion-bancaria';
import Promociones from './pages/promociones';
import MisPromociones from './pages/mis-promociones';
import NewSale from './pages/nuevo-registro';
import LivenessDetection from './pages/liveness-detection';
import { GoogleMapsProvider } from './context/GoogleMapsProvider';
import CambiarEmail from './pages/tiendas/cambiar-email';
import Recargar from './pages/tiendas/recargar';
import Recargas from './pages/reportes/recargas';
import WarrantyExchange from './pages/cambio-garantia';
import VentasOnline from './pages/ventas-online';
import Inventario from './pages/inventario';
import Catalogos from './pages/catalogos';
import BuscarIphones from './pages/tiendas/buscar-iphones';
import SellerCommissions from './pages/comisiones-vendedores';
import Salud from './pages/salud';
import Integraciones from './pages/integraciones';
import Perfil from './pages/perfil';

document.documentElement.classList.remove('dark', 'light');
document.documentElement.classList.add(getInitialTheme());

// iOS Safari scrolls window itself (not the body, despite overflow:hidden) to
// bring focused inputs above the on-screen keyboard, and frequently fails to
// scroll back to 0 when the keyboard dismisses — leaving content shifted up
// over the status bar with empty space at the bottom. The triggers are many:
// blur, input unmount mid-keyboard, route transitions, bfcache restore, etc.
//
// Rather than enumerate every trigger, enforce the invariant directly: when
// no input is focused, window.scrollY must be 0 (body has overflow:hidden, so
// any non-zero state is wrong). A scroll listener catches it the moment iOS
// applies the bad scroll, and a short RAF watchdog after each plausible
// trigger holds the page at 0 across keyboard-close animations.
(function enforceIOSScrollInvariant() {
	const ua = navigator.userAgent;
	const isIOS = /iPad|iPhone|iPod/.test(ua) || (ua.includes('Mac') && 'ontouchend' in document);
	if (!isIOS) return;

	const isEditable = (el: Element | null): boolean => {
		if (!el) return false;
		const tag = el.tagName;
		if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return true;
		return (el as HTMLElement).isContentEditable === true;
	};

	let frameId = 0;

	// One frame of watchdog. Every frame: if no input is focused and the
	// window is scrolled, snap it back. If we corrected anything, extend the
	// window — iOS keyboard-close animations can re-apply the bad scroll over
	// several frames. Stops the moment an input gets focus (let iOS scroll
	// inputs above the keyboard while typing).
	const tick = (framesLeft: number) => {
		if (isEditable(document.activeElement)) {
			frameId = 0;
			return;
		}
		let frames = framesLeft;
		if (window.scrollY !== 0 || window.scrollX !== 0) {
			window.scrollTo(0, 0);
			frames = 30;
		}
		frameId = frames > 0 ? requestAnimationFrame(() => tick(frames - 1)) : 0;
	};

	// Start a 30-frame (~500ms) watchdog. If one is already running, restart it.
	const watch = () => {
		if (frameId) cancelAnimationFrame(frameId);
		frameId = requestAnimationFrame(() => tick(30));
	};

	const stopWatch = () => {
		if (frameId) {
			cancelAnimationFrame(frameId);
			frameId = 0;
		}
	};

	// Direct catch: any window scroll change kicks the watchdog. When iOS
	// applies its bad scroll, we see it immediately and undo it (unless an
	// input is focused, in which case the scroll is intentional).
	window.addEventListener('scroll', watch, { passive: true });

	// Focus changes: blur may have left a stale scroll; focus means stop
	// fighting iOS while the user is typing.
	document.addEventListener('focusout', watch);
	document.addEventListener('focusin', stopWatch);

	// Initial-state cases: pageshow covers first load + bfcache restore (back/
	// forward navigation, PWA resume). orientationchange is another known way
	// iOS leaves the page in a bad scroll state.
	window.addEventListener('pageshow', watch);
	window.addEventListener('orientationchange', watch);

	// The visual viewport tracks the on-screen keyboard directly — fires even
	// when focusout doesn't (e.g. focused input unmounted by a render).
	if (window.visualViewport) {
		window.visualViewport.addEventListener('resize', watch);
		window.visualViewport.addEventListener('scroll', watch);
	}

	// Kick a watchdog immediately to fix any state inherited from navigation.
	watch();
})();

const App = () => {
	return (
		<GoogleMapsProvider>
			<Router>
				<Layout>
					<Routes>
						<Route path="/login" element={<LoginForm />} />
						<Route path="/" element={<Inicio />} />
						<Route path="/buscarcliente" element={<Pay />} />
						<Route path="/newsale" element={<NewSale key="sale" />} />
						<Route path="/simulator" element={<NewSale key="simulator" simulator={true} />} />
						<Route path="/cpc" element={<CPC />} />
						<Route path="/tiendas/eliminar-pago" element={<DeletePayment />} />
						<Route path="/tiendas/cambiar-precio" element={<ChangePrice />} />
						<Route path="/deleteSale" element={<DeleteSale />} />
						<Route path="/warranty" element={<WarrantyExchange />} />
						<Route path="/reportes/ventas" element={<Ventas />} />
						<Route path="/reportes/transacciones" element={<Transacciones />} />
						<Route path="/reportes/registros-eliminados" element={<Eliminados />} />
						<Route path="/reportes/delincuentes" element={<DelinquentUsers />} />
						<Route path="/salud" element={<Salud />} />
						<Route path="/integraciones" element={<Integraciones />} />
						<Route path="/recuperacion-cartera" element={<RecuperacionCartera />} />
						<Route path="/reportes/recargas" element={<Recargas />} />
						<Route path="/sucursales" element={<Sucursales />} />
						<Route path="/tiendas/cambiar-email" element={<CambiarEmail />} />
						<Route path="/tiendas/cambiar-telefono" element={<CambiarTelefono />} />
						<Route path="/tiendas/recargar" element={<Recargar />} />
						<Route path="/metodos-de-pago" element={<MetodosDePago />} />
						<Route path="/informacion-bancaria" element={<InformationBancaria />} />
						<Route path="/internal" element={<Internal />} />
						<Route path="/log-in-as" element={<LogInAs />} />
						<Route path="/ayuda" element={<Help />} />
						<Route path="/promociones" element={<Promociones />} />
						<Route path="/mis-promociones" element={<MisPromociones />} />
						<Route path="/liveness-detection" element={<LivenessDetection />} />
						<Route path="/ventas-online" element={<VentasOnline />} />
						<Route path="/ventas-online/inventario" element={<Inventario />} />
						<Route path="/ventas-online/catalogos" element={<Catalogos />} />
						<Route path="/tiendas/buscar-iphones" element={<BuscarIphones />} />
						<Route path="/comisiones-vendedores" element={<SellerCommissions />} />
						<Route path="/perfil" element={<Perfil />} />
					</Routes>
				</Layout>
			</Router>
		</GoogleMapsProvider>
	);
};

function getInitialTheme() {
	try {
		const savedTheme = localStorage.getItem('theme');
		if (savedTheme === 'dark' || savedTheme === 'light') return savedTheme;
	} catch {
		// A third-party iframe can be denied browser storage. Keep login/fallback usable.
	}
	return 'dark';
}

export default App;
